/**
 * DepositInvoiceBanner — "มัดจำชำระแล้วตาม INV-… ฿X (ชำระวันที่ D)" ในหน้ารายการเช่า (FB-2026-0018 · มติ PO 3 พ.ย. 2026)
 *
 * - backend หาใบให้ (`GET /rentals/{id}/deposit-invoices`): สัญญา → ใบงานเตรียมเครื่อง → ใบเสนอราคา → ใบแจ้งหนี้รายการเดียว
 *   ที่ชำระครบแล้ว · หาเส้นทางไม่เจอ = ไม่แสดงอะไร (ไม่เดาจากบริษัท/ยอดเงิน)
 * - มัดจำในสัญญา **ไม่พลิกเอง** เมื่อรับชำระ → ปุ่ม "บันทึกรับมัดจำจากใบนี้" (สิทธิ์ rentals.deposit_manage) เรียก
 *   `deposit_service.mark_paid` ตัวเดิมทีละรายการที่ยังรอชำระ · วันที่รับ = วันชำระของใบแจ้งหนี้
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RiBillLine, RiCheckLine } from 'react-icons/ri';
import depositService, { RentalDepositInvoice } from '../../services/depositService';
import { usePermission } from '../../hooks/usePermission';
import { showAlert, showConfirm } from '../../utils/alert';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { formatDate } from '../../utils/dateUtils';

const money = (v: number | string | null | undefined): string => {
  const n = typeof v === 'string' ? parseFloat(v) : v ?? 0;
  return (Number.isFinite(n) ? (n as number) : 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

interface Props {
  rentalId: number;
  /** เปลี่ยนค่าเมื่อหน้าโหลดใหม่ (เช่น object สรุปมัดจำ) → ดึงใบใหม่ */
  refreshTrigger?: unknown;
  /** หลังบันทึกรับมัดจำสำเร็จ — หน้าโหลด rental/timeline/สรุปมัดจำใหม่ */
  onRecorded?: () => void;
}

const DepositInvoiceBanner: React.FC<Props> = ({ rentalId, refreshTrigger, onRecorded }) => {
  const { hasPermission } = usePermission();
  const [invoices, setInvoices] = useState<RentalDepositInvoice[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    depositService.getDepositInvoices(rentalId)
      .then((rows) => { if (!cancelled) setInvoices(rows); })
      .catch((err) => {
        // ไม่ critical — ไม่มีแถบก็แค่บันทึกรับมัดจำทีละรายการทางเดิม
        console.error('Error loading deposit invoices:', err);
        if (!cancelled) setInvoices([]);
      });
    return () => { cancelled = true; };
  }, [rentalId, refreshTrigger]);

  if (invoices.length === 0) return null;
  const canManage = hasPermission('rentals.deposit_manage');

  const record = (inv: RentalDepositInvoice) => {
    showConfirm(
      'บันทึกรับมัดจำจากใบนี้',
      `บันทึกว่ารับมัดจำแล้ว ${inv.pending_rental_item_ids.length} รายการ (฿${money(inv.pending_deposit_total)}) ` +
        `วันที่รับ ${formatDate(inv.paid_date || '')} ตามใบแจ้งหนี้ ${inv.invoice_number}?`,
      async () => {
        setBusyId(inv.invoice_id);
        try {
          const res = await depositService.markPaidFromInvoice(rentalId, inv.invoice_id);
          showAlert(
            'บันทึกรับมัดจำแล้ว',
            res.warnings.length > 0
              ? `${res.rental_item_ids.length} รายการ · ${res.warnings.join(' · ')}`
              : `${res.rental_item_ids.length} รายการ ตามใบแจ้งหนี้ ${res.invoice_number}`,
            res.warnings.length > 0 ? 'warning' : 'success'
          );
          onRecorded?.();
        } catch (err) {
          onRecorded?.(); // สถานะมัดจำอาจถูกเปลี่ยนจากที่อื่นแล้ว
          showAlert('บันทึกไม่สำเร็จ', getApiErrorMessage(err, 'ไม่สามารถบันทึกรับมัดจำได้'), 'error');
        } finally {
          setBusyId(null);
        }
      }
    );
  };

  return (
    <div className="space-y-2" data-testid="deposit-invoice-banner">
      {invoices.map((inv) => (
        <div key={inv.invoice_id} className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm">
          <div className="flex items-start gap-2">
            <RiBillLine className="w-4 h-4 text-success-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0 text-success-800">
              มัดจำชำระแล้วตาม{' '}
              {hasPermission('invoices.view') ? (
                <Link to={`/billing/invoices?view=${inv.invoice_id}`} className="font-semibold underline">
                  {inv.invoice_number}
                </Link>
              ) : (
                <span className="font-semibold">{inv.invoice_number}</span>
              )}{' '}
              ฿{money(inv.total_amount)}
              {inv.paid_date && <> (ชำระวันที่ {formatDate(inv.paid_date)})</>}
              {inv.source_quotation_number && (
                <span className="block text-xs text-success-700 mt-0.5">จากใบเสนอราคา {inv.source_quotation_number}</span>
              )}
              {inv.pending_rental_item_ids.length === 0 && (
                <span className="block text-xs text-success-700 mt-0.5 inline-flex items-center gap-1">
                  <RiCheckLine className="w-3.5 h-3.5" /> บันทึกรับมัดจำในรายการเช่าแล้ว
                </span>
              )}
            </div>
          </div>
          {inv.pending_rental_item_ids.length > 0 && canManage && (
            <button
              type="button"
              onClick={() => record(inv)}
              disabled={busyId !== null}
              className="mt-2 w-full sm:w-auto px-3 py-1.5 text-xs font-medium text-white bg-success-600 rounded-md hover:bg-success-700 disabled:opacity-50"
            >
              {busyId === inv.invoice_id
                ? 'กำลังบันทึก...'
                : `บันทึกรับมัดจำจากใบนี้ (${inv.pending_rental_item_ids.length} รายการ)`}
            </button>
          )}
          {inv.pending_rental_item_ids.length > 0 && !canManage && (
            <p className="mt-1 text-xs text-success-700">
              มัดจำ {inv.pending_rental_item_ids.length} รายการยังเป็น &quot;รอชำระ&quot; — ผู้มีสิทธิ์จัดการมัดจำ (บัญชี/ผู้จัดการ) เป็นผู้บันทึกรับ
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default DepositInvoiceBanner;
